/**
 * @fullvsl/app-shell/project-metrics — deterministic per-VSL analytics summary.
 *
 * The approved dashboard design system (fullvsl_dashboard) is analytics-first:
 * the inventory table and KPI row show performance data (plays, play rate, CTA
 * rate, completion) — not config metadata. To make the app shell read as a real
 * operational VSL product (not a config demo), every project on the dashboard
 * needs a stable set of performance metrics.
 *
 * This module derives deterministic, reproducible metrics from a project's id +
 * name so seeded AND user-created VSLs both surface plausible numbers. It
 * mirrors the shape of the backend `AnalyticsOverview` so the swap to the live
 * `GET /api/v1/vsls/:id/analytics` is a drop-in. Numbers are clearly fixture
 * data (the analytics view already carries a "fixture data" banner).
 */

export interface ProjectMetrics {
  /** Unique visitors who loaded the player. */
  impressions: number;
  /** Visitors who started playback. */
  plays: number;
  /** plays / impressions (0–1). */
  playRate: number;
  /** Conversion proxy: cta_clicked / cta_shown (0–1). */
  ctaClickRate: number;
  /** Average watch-through percent (0–1) → drives the completion bar. */
  completionRate: number;
  /** Period-over-period trend for plays (signed, 0–1). */
  playRateTrend: number;
}

const PT_BR_INTEGER = new Intl.NumberFormat("pt-BR");
const PT_BR_PERCENT = new Intl.NumberFormat("pt-BR", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const PT_BR_TREND = new Intl.NumberFormat("pt-BR", {
  style: "percent",
  signDisplay: "always",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Seeded PRNG (mulberry32) for deterministic metrics. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash a string into a 32-bit seed (FNV-1a). */
function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Derive stable, plausible metrics for a project.
 *
 * Drafts surface reduced/pending numbers so the dashboard still reads like a
 * real product while making the publish state visible.
 */
export function getProjectMetrics(
  id: string,
  name: string,
  status: "draft" | "published" = "published",
): ProjectMetrics {
  const rng = mulberry32(hashSeed(`${id}:${name}`));
  const draft = status === "draft";

  const baseImpressions = draft
    ? 20 + Math.floor(rng() * 80)
    : 8_000 + Math.floor(rng() * 480_000);
  const playRate = draft
    ? 0
    : 0.55 + rng() * 0.3; // 55–85%
  const plays = draft ? 0 : Math.round(baseImpressions * playRate);
  const ctaClickRate = draft
    ? 0
    : 0.03 + rng() * 0.06; // 3–9%
  const completionRate = draft
    ? 0
    : 0.12 + rng() * 0.18; // 12–30%
  const playRateTrend = draft
    ? 0
    : (rng() - 0.35) * 0.16; // mostly up, occasionally down

  return {
    impressions: baseImpressions,
    plays,
    playRate,
    ctaClickRate,
    completionRate,
    playRateTrend,
  };
}

/** Format a whole number with thousands separators (e.g. 428210 → "428,210"). */
export function fmtNumber(n: number): string {
  return PT_BR_INTEGER.format(Math.round(n));
}

/** Compact a large number (e.g. 1240000 → "1.24M", 89204 → "89,204"). */
export function fmtCompact(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${(m >= 10 ? Math.round(m) : Math.round(m * 100) / 100).toLocaleString("pt-BR")}M`;
  }
  if (n >= 10_000) {
    return `${Math.round(n / 1000)}K`;
  }
  return fmtNumber(n);
}

/** Format a 0–1 ratio as a percentage with one decimal (e.g. 0.624 → "62.4%"). */
export function fmtPct(ratio: number): string {
  return PT_BR_PERCENT.format(ratio);
}

/** Format a signed 0–1 trend as a compact delta (e.g. +12%, -1.1%). */
export function fmtTrend(ratio: number): string {
  return PT_BR_TREND.format(ratio);
}
